<template>
  <FlashNotifications ref="flash" />

  <div class="min-h-screen bg-gray-50 dark:bg-[#0d1117]">

    <!-- ══ LOADING ══ -->
    <template v-if="loading">
      <!-- Mobile skeleton -->
      <div class="sm:hidden animate-pulse bg-gray-50 min-h-screen">
        <div class="bg-white border-b border-gray-100 px-4 flex items-center justify-between h-14">
          <div class="h-6 bg-gray-200 rounded-lg w-28"></div>
          <div class="h-8 bg-gray-200 rounded-full w-24"></div>
        </div>
        <div class="px-4 pt-5 space-y-3">
          <div v-for="i in 4" :key="i" class="bg-white rounded-2xl p-4 flex items-center gap-3">
            <div class="w-9 h-9 bg-gray-200 rounded-full flex-shrink-0"></div>
            <div class="flex-1 space-y-1.5">
              <div class="h-4 bg-gray-200 rounded w-32"></div>
              <div class="h-3 bg-gray-100 rounded w-24"></div>
            </div>
            <div class="flex gap-2">
              <div class="w-8 h-8 bg-gray-100 rounded-lg"></div>
              <div class="w-8 h-8 bg-gray-100 rounded-lg"></div>
            </div>
          </div>
        </div>
      </div>
      <!-- Desktop skeleton -->
      <div class="hidden sm:block animate-pulse">
        <div class="bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700">
          <div class="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
            <div class="space-y-2">
              <div class="h-6 bg-gray-200 dark:bg-gray-700 rounded w-28"></div>
              <div class="h-3 bg-gray-100 dark:bg-gray-800 rounded w-52"></div>
            </div>
            <div class="h-9 bg-gray-200 dark:bg-gray-700 rounded-lg w-36"></div>
          </div>
        </div>
        <div class="max-w-4xl mx-auto px-6 py-6 space-y-3">
          <div v-for="i in 4" :key="i"
            class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 px-5 py-4 flex items-center gap-4">
            <div class="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full flex-shrink-0"></div>
            <div class="flex-1 space-y-1.5">
              <div class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-40"></div>
              <div class="h-3 bg-gray-100 dark:bg-gray-800 rounded w-28"></div>
            </div>
            <div class="flex gap-2">
              <div class="w-20 h-8 bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
              <div class="w-20 h-8 bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- ══ ERROR ══ -->
    <template v-else-if="error">
      <div class="flex flex-col items-center justify-center py-32 text-center px-4">
        <div class="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
          <svg class="w-8 h-8 text-red-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/>
          </svg>
        </div>
        <h3 class="text-base font-semibold text-gray-900 dark:text-white mb-1">Something went wrong</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 mb-5">{{ error }}</p>
        <button @click="() => { store.invalidate(); store.fetchIfStale() }"
          class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg text-sm transition-colors">
          Try again
        </button>
      </div>
    </template>

    <!-- ══ LOADED ══ -->
    <template v-else>

      <!-- ── MOBILE ── -->
      <div class="sm:hidden bg-gray-50 min-h-screen">
        <div class="sticky top-0 z-10 bg-white border-b border-gray-100">
          <!-- Title row -->
          <div class="flex items-center justify-between px-4 h-14">
            <h1 class="text-[22px] font-bold text-gray-900 tracking-tight leading-none">Holidays</h1>
            <button @click="openAddModal" type="button"
              class="flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 text-white text-sm font-semibold rounded-full active:opacity-80 transition-opacity">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
              </svg>
              Add
            </button>
          </div>
          <!-- Search -->
          <div class="px-4 pb-3">
            <div class="relative">
              <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/>
              </svg>
              <input v-model="searchQuery" type="search" placeholder="Search holidays..."
                class="w-full bg-gray-100 rounded-xl pl-9 pr-4 py-2 text-sm text-gray-900 placeholder-gray-400 border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all" />
            </div>
          </div>
          <!-- Date range + sort row -->
          <div class="flex items-center gap-2 px-4 pb-3">
            <!-- Single calendar date range picker -->
            <v-date-picker
              v-model="dateRange"
              mode="date"
              is-range
              :locale="CURRENT_LOCALE"
              :is-dark="isDark"
              :popover="{ visibility: 'focus' }"
              class="flex-1">
              <template #default="{ inputValue, inputEvents }">
                <button type="button"
                  class="w-full flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-200 rounded-xl bg-gray-50 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-left"
                  v-on="inputEvents.start">
                  <svg class="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"/>
                  </svg>
                  <span class="truncate">{{ dateRangeLabel }}</span>
                </button>
              </template>
            </v-date-picker>

            <!-- Sort dropdown -->
            <select v-model="sortBy"
              class="px-2.5 py-2 text-sm border border-gray-200 rounded-xl bg-gray-50 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer flex-shrink-0">
              <option value="date_asc">Soonest</option>
              <option value="date_desc">Latest</option>
              <option value="name_asc">A–Z</option>
              <option value="name_desc">Z–A</option>
            </select>

            <button v-if="hasActiveFilters" @click="clearFilters" type="button"
              class="text-xs text-blue-600 font-semibold flex-shrink-0">
              Reset
            </button>
          </div>
        </div>

        <!-- Empty state (no holidays at all) -->
        <div v-if="store.holidays.length === 0"
          class="flex flex-col items-center justify-center py-20 px-6 text-center">
          <!-- Beach illustration -->
          <div class="mb-5 relative">
            <svg width="120" height="100" viewBox="0 0 120 100" fill="none" xmlns="http://www.w3.org/2000/svg">
              <!-- Sky gradient background -->
              <rect width="120" height="70" rx="12" fill="url(#skyMobile)"/>
              <!-- Sun -->
              <circle cx="88" cy="22" r="12" fill="#FCD34D"/>
              <circle cx="88" cy="22" r="8" fill="#FBBF24"/>
              <!-- Ocean -->
              <rect x="0" y="55" width="120" height="30" rx="0" fill="url(#seaMobile)"/>
              <rect x="0" y="55" width="120" height="8" fill="#38BDF8" opacity="0.5"/>
              <!-- Wave lines -->
              <path d="M0 62 Q15 58 30 62 Q45 66 60 62 Q75 58 90 62 Q105 66 120 62" stroke="white" stroke-width="1.5" stroke-opacity="0.6" fill="none"/>
              <!-- Sand -->
              <ellipse cx="60" cy="84" rx="60" ry="16" fill="#FDE68A"/>
              <rect x="0" y="80" width="120" height="20" fill="#FDE68A"/>
              <!-- Palm trunk -->
              <path d="M42 80 Q44 60 40 42 Q38 30 35 20" stroke="#92400E" stroke-width="4" stroke-linecap="round" fill="none"/>
              <!-- Palm leaves -->
              <path d="M35 20 Q20 10 10 18" stroke="#16A34A" stroke-width="3" stroke-linecap="round" fill="none"/>
              <path d="M35 20 Q28 5 40 0" stroke="#16A34A" stroke-width="3" stroke-linecap="round" fill="none"/>
              <path d="M35 20 Q48 8 55 15" stroke="#16A34A" stroke-width="3" stroke-linecap="round" fill="none"/>
              <path d="M35 20 Q22 22 14 30" stroke="#22C55E" stroke-width="2.5" stroke-linecap="round" fill="none"/>
              <path d="M35 20 Q45 18 52 26" stroke="#22C55E" stroke-width="2.5" stroke-linecap="round" fill="none"/>
              <!-- Coconuts -->
              <circle cx="38" cy="26" r="3" fill="#92400E"/>
              <circle cx="33" cy="23" r="2.5" fill="#A16207"/>
              <!-- Umbrella pole -->
              <line x1="80" y1="78" x2="75" y2="48" stroke="#6B7280" stroke-width="2" stroke-linecap="round"/>
              <!-- Umbrella top -->
              <path d="M60 48 Q75 38 90 48" stroke="#EF4444" stroke-width="2" fill="#FCA5A5" stroke-linejoin="round"/>
              <path d="M62 48 Q75 55 88 48" stroke="#EF4444" stroke-width="1" fill="#FEE2E2"/>
              <!-- Towel on sand -->
              <rect x="55" y="76" width="30" height="8" rx="2" fill="#60A5FA" opacity="0.8"/>
              <line x1="55" y1="79" x2="85" y2="79" stroke="white" stroke-width="0.8" stroke-opacity="0.6"/>
              <defs>
                <linearGradient id="skyMobile" x1="0" y1="0" x2="0" y2="70" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stop-color="#BAE6FD"/>
                  <stop offset="100%" stop-color="#7DD3FC"/>
                </linearGradient>
                <linearGradient id="seaMobile" x1="0" y1="55" x2="0" y2="85" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stop-color="#0EA5E9"/>
                  <stop offset="100%" stop-color="#0284C7"/>
                </linearGradient>
              </defs>
            </svg>
          </div>
          <h2 class="text-xl font-bold text-gray-900 mb-1.5">No holidays planned</h2>
          <p class="text-sm text-gray-500 max-w-[220px] mb-6">Block off your vacation days so clients know when you are away.</p>
          <button @click="openAddModal" type="button"
            class="px-6 py-3 bg-blue-600 text-white font-semibold rounded-2xl text-sm shadow-md shadow-blue-600/30 active:opacity-80 transition-opacity">
            Add holiday
          </button>
        </div>

        <!-- No results from filter -->
        <div v-else-if="filteredHolidays.length === 0"
          class="flex flex-col items-center justify-center py-20 px-6 text-center">
          <div class="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-4">
            <svg class="w-7 h-7 text-gray-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/>
            </svg>
          </div>
          <p class="font-semibold text-gray-900 mb-1">No holidays found</p>
          <p class="text-sm text-gray-400 mb-4">Try a different search or date range.</p>
          <button @click="clearFilters" type="button" class="text-blue-600 font-semibold text-sm">Clear filters</button>
        </div>

        <!-- List -->
        <div v-else class="px-4 pt-4 pb-20 space-y-2.5">
          <div v-for="holiday in filteredHolidays" :key="holiday.id"
            class="bg-white rounded-2xl px-4 py-3.5 flex items-center gap-3"
            :class="holiday.is_holiday_spent ? 'opacity-60' : ''">
            <span class="w-9 h-9 rounded-full flex-shrink-0"
              :style="{ backgroundColor: colorHex(holiday.color) }"></span>
            <div class="flex-1 min-w-0">
              <p class="text-[15px] font-semibold text-gray-900 truncate"
                :class="holiday.is_holiday_spent ? 'line-through text-gray-500' : ''">
                {{ holiday.name }}
              </p>
              <p class="text-xs text-gray-400 mt-0.5">
                {{ holiday.formatted_start_date }}
                <span v-if="holiday.formatted_start_date !== holiday.formatted_end_date">
                  – {{ holiday.formatted_end_date }}
                </span>
              </p>
            </div>
            <div class="flex items-center gap-1.5 flex-shrink-0">
              <button @click="openEditModal(holiday)" type="button"
                class="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Z"/>
                </svg>
              </button>
              <button @click="openDeleteModal(holiday)" type="button"
                class="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- ── DESKTOP ── -->
      <div class="hidden sm:block">
        <div class="bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700">
          <div class="max-w-4xl mx-auto px-6">

            <!-- Title row -->
            <div class="flex items-center justify-between pt-5 pb-3">
              <div>
                <div class="flex items-center gap-3">
                  <h1 class="text-sm font-semibold text-gray-900 dark:text-white leading-none">Holidays</h1>
                  <span v-if="store.holidays.length > 0"
                    class="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 tabular-nums">
                    {{ filteredHolidays.length }}{{ filteredHolidays.length !== store.holidays.length ? ` of ${store.holidays.length}` : '' }}
                  </span>
                </div>
                <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-none">Block out days when you are unavailable</p>
              </div>
              <button @click="openAddModal" type="button"
                class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm flex-shrink-0">
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
                </svg>
                Add holiday
              </button>
            </div>

            <!-- Toolbar: search + date range + sort + reset -->
            <div class="flex flex-wrap items-center gap-2 pb-4">

              <!-- Search -->
              <div class="relative flex-1 min-w-40">
                <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/>
                </svg>
                <input v-model="searchQuery" type="search" placeholder="Search holidays..."
                  class="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow" />
              </div>

              <!-- Date range — single calendar popover -->
              <v-date-picker
                v-model="dateRange"
                mode="date"
                is-range
                :locale="CURRENT_LOCALE"
                :is-dark="isDark"
                :popover="{ visibility: 'focus' }">
                <template #default="{ inputValue, inputEvents }">
                  <button type="button"
                    class="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 whitespace-nowrap"
                    v-on="inputEvents.start">
                    <svg class="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"/>
                    </svg>
                    {{ dateRangeLabel }}
                    <svg class="w-3 h-3 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" clip-rule="evenodd"/>
                    </svg>
                  </button>
                </template>
              </v-date-picker>

              <!-- Sort dropdown -->
              <select v-model="sortBy"
                class="pl-3 pr-7 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer transition-colors">
                <option value="date_asc">Date: soonest first</option>
                <option value="date_desc">Date: latest first</option>
                <option value="name_asc">Name: A to Z</option>
                <option value="name_desc">Name: Z to A</option>
              </select>

              <!-- Reset -->
              <button v-if="hasActiveFilters" @click="clearFilters" type="button"
                class="text-xs text-blue-600 dark:text-blue-400 font-semibold hover:text-blue-700 transition-colors whitespace-nowrap">
                Reset
              </button>

            </div>

          </div>
        </div>

        <div class="max-w-4xl mx-auto px-6 py-6">

          <!-- No results from filter -->
          <div v-if="store.holidays.length > 0 && filteredHolidays.length === 0"
            class="flex flex-col items-center justify-center py-20 text-center">
            <div class="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
              <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/>
              </svg>
            </div>
            <h3 class="text-base font-semibold text-gray-900 dark:text-white mb-1">No holidays found</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400 mb-5">Try a different search or adjust the date range.</p>
            <button @click="clearFilters" type="button"
              class="px-4 py-2 text-sm font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 transition-colors">
              Clear filters
            </button>
          </div>

          <!-- Empty state (no holidays at all) -->
          <div v-else-if="store.holidays.length === 0"
            class="flex flex-col items-center justify-center py-24 text-center">
            <!-- Beach illustration (desktop — wider) -->
            <div class="mb-6 relative">
              <svg width="200" height="130" viewBox="0 0 200 130" fill="none" xmlns="http://www.w3.org/2000/svg">
                <!-- Sky -->
                <rect width="200" height="90" rx="16" fill="url(#skyDesktop)"/>
                <!-- Sun + rays -->
                <circle cx="158" cy="28" r="18" fill="#FCD34D" opacity="0.3"/>
                <circle cx="158" cy="28" r="13" fill="#FCD34D"/>
                <circle cx="158" cy="28" r="9" fill="#FBBF24"/>
                <!-- Clouds -->
                <ellipse cx="35" cy="20" rx="18" ry="8" fill="white" opacity="0.7"/>
                <ellipse cx="50" cy="16" rx="14" ry="7" fill="white" opacity="0.7"/>
                <!-- Ocean -->
                <rect x="0" y="72" width="200" height="40" fill="url(#seaDesktop)"/>
                <rect x="0" y="72" width="200" height="10" fill="#38BDF8" opacity="0.4"/>
                <!-- Waves -->
                <path d="M0 82 Q25 77 50 82 Q75 87 100 82 Q125 77 150 82 Q175 87 200 82" stroke="white" stroke-width="1.5" stroke-opacity="0.5" fill="none"/>
                <path d="M0 90 Q30 86 60 90 Q90 94 120 90 Q150 86 180 90 Q195 93 200 90" stroke="white" stroke-width="1" stroke-opacity="0.3" fill="none"/>
                <!-- Sand -->
                <ellipse cx="100" cy="118" rx="100" ry="18" fill="#FDE68A"/>
                <rect x="0" y="108" width="200" height="22" fill="#FDE68A"/>
                <!-- Palm trunk left -->
                <path d="M55 108 Q58 88 53 68 Q50 52 46 36" stroke="#92400E" stroke-width="5" stroke-linecap="round" fill="none"/>
                <!-- Palm leaves left -->
                <path d="M46 36 Q28 22 14 32" stroke="#16A34A" stroke-width="3.5" stroke-linecap="round" fill="none"/>
                <path d="M46 36 Q38 18 52 10" stroke="#16A34A" stroke-width="3.5" stroke-linecap="round" fill="none"/>
                <path d="M46 36 Q62 20 72 28" stroke="#16A34A" stroke-width="3.5" stroke-linecap="round" fill="none"/>
                <path d="M46 36 Q30 38 20 50" stroke="#22C55E" stroke-width="3" stroke-linecap="round" fill="none"/>
                <path d="M46 36 Q60 32 68 44" stroke="#22C55E" stroke-width="3" stroke-linecap="round" fill="none"/>
                <!-- Coconuts left -->
                <circle cx="50" cy="44" r="4" fill="#92400E"/>
                <circle cx="44" cy="40" r="3.5" fill="#A16207"/>
                <!-- Palm trunk right -->
                <path d="M148 108 Q145 90 150 70 Q153 55 158 42" stroke="#78350F" stroke-width="4.5" stroke-linecap="round" fill="none"/>
                <!-- Palm leaves right -->
                <path d="M158 42 Q172 28 186 36" stroke="#15803D" stroke-width="3" stroke-linecap="round" fill="none"/>
                <path d="M158 42 Q162 25 175 28" stroke="#15803D" stroke-width="3" stroke-linecap="round" fill="none"/>
                <path d="M158 42 Q145 28 138 36" stroke="#15803D" stroke-width="3" stroke-linecap="round" fill="none"/>
                <path d="M158 42 Q170 44 178 54" stroke="#16A34A" stroke-width="2.5" stroke-linecap="round" fill="none"/>
                <path d="M158 42 Q145 46 140 56" stroke="#16A34A" stroke-width="2.5" stroke-linecap="round" fill="none"/>
                <!-- Coconuts right -->
                <circle cx="154" cy="50" r="3.5" fill="#92400E"/>
                <circle cx="161" cy="48" r="3" fill="#A16207"/>
                <!-- Beach umbrella -->
                <line x1="100" y1="108" x2="96" y2="68" stroke="#6B7280" stroke-width="2.5" stroke-linecap="round"/>
                <path d="M76 68 Q96 56 116 68" stroke="#EF4444" stroke-width="2" fill="#FCA5A5"/>
                <path d="M78 68 Q96 76 114 68" stroke="#EF4444" stroke-width="1" fill="#FEE2E2"/>
                <!-- Towel / mat -->
                <rect x="80" y="104" width="44" height="10" rx="3" fill="#60A5FA" opacity="0.9"/>
                <line x1="80" y1="108" x2="124" y2="108" stroke="white" stroke-width="1" stroke-opacity="0.5"/>
                <!-- Flip flops -->
                <ellipse cx="72" cy="110" rx="5" ry="3" fill="#F97316" opacity="0.8" transform="rotate(-15 72 110)"/>
                <ellipse cx="66" cy="112" rx="5" ry="3" fill="#FB923C" opacity="0.8" transform="rotate(-10 66 112)"/>
                <defs>
                  <linearGradient id="skyDesktop" x1="0" y1="0" x2="0" y2="90" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stop-color="#BAE6FD"/>
                    <stop offset="100%" stop-color="#7DD3FC"/>
                  </linearGradient>
                  <linearGradient id="seaDesktop" x1="0" y1="72" x2="0" y2="112" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stop-color="#0EA5E9"/>
                    <stop offset="100%" stop-color="#0369A1"/>
                  </linearGradient>
                </defs>
              </svg>
            </div>
            <h2 class="text-xl font-bold text-gray-900 dark:text-white mb-2">No holidays planned</h2>
            <p class="text-sm text-gray-500 dark:text-gray-400 max-w-xs mb-8">
              Block off your vacation days so clients know when you are away and cannot be booked.
            </p>
            <button @click="openAddModal" type="button"
              class="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg text-sm shadow-sm transition-colors">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
              </svg>
              Add your first holiday
            </button>
          </div>

          <!-- Holiday list -->
          <div v-else class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
            <div v-for="(holiday, index) in filteredHolidays" :key="holiday.id"
              class="flex items-center gap-4 px-5 py-4 transition-colors hover:bg-gray-50/70 dark:hover:bg-gray-800/40"
              :class="[
                index < filteredHolidays.length - 1 ? 'border-b border-gray-100 dark:border-gray-700/70' : '',
                holiday.is_holiday_spent ? 'opacity-55' : '',
              ]">

              <!-- Color circle -->
              <span class="w-10 h-10 rounded-full flex-shrink-0"
                :style="{ backgroundColor: colorHex(holiday.color) }"></span>

              <!-- Name + date -->
              <div class="flex-1 min-w-0">
                <p class="font-semibold text-gray-900 dark:text-gray-100 text-[15px] truncate"
                  :class="holiday.is_holiday_spent ? 'line-through text-gray-400 dark:text-gray-500' : ''">
                  {{ holiday.name }}
                </p>
                <p class="text-sm text-gray-400 dark:text-gray-500 mt-0.5 tabular-nums">
                  {{ holiday.formatted_start_date }}
                  <span v-if="holiday.formatted_start_date !== holiday.formatted_end_date">
                    – {{ holiday.formatted_end_date }}
                  </span>
                </p>
              </div>

              <!-- Past badge -->
              <span v-if="holiday.is_holiday_spent"
                class="text-[11px] font-semibold text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-full flex-shrink-0">
                Past
              </span>

              <!-- Actions -->
              <div class="flex items-center gap-1.5 flex-shrink-0">
                <button @click="openEditModal(holiday)" type="button"
                  class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Z"/>
                  </svg>
                  Edit
                </button>
                <button @click="openDeleteModal(holiday)" type="button"
                  class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 bg-white dark:bg-gray-800 border border-red-200 dark:border-red-800/50 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/>
                  </svg>
                  Delete
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>
    </template>

    <!-- ── Add / Edit modal ── -->
    <Transition name="modal">
      <div v-if="formModal.open"
        class="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
        @click.self="formModal.open = false">
        <div class="absolute inset-0 bg-black/40 backdrop-blur-sm"></div>
        <div class="relative bg-white dark:bg-[#1C2128] w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-2xl border border-gray-100 dark:border-gray-700 animate-in">

          <!-- Header -->
          <div class="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100 dark:border-gray-700">
            <h3 class="text-lg font-bold text-gray-900 dark:text-white">
              {{ formModal.isEdit ? 'Edit holiday' : 'Add holiday' }}
            </h3>
            <button @click="formModal.open = false" type="button"
              class="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <form @submit.prevent="submitForm" class="px-6 py-5 space-y-5">

            <!-- Name -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Name</label>
              <input v-model="form.name" type="text" required
                placeholder="e.g. Christmas Day"
                class="w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-[#22272E] text-gray-900 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow" />
            </div>

            <!-- Date range -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Date</label>
              <v-date-picker
                v-model="targetDate"
                :key="formKey"
                :locale="CURRENT_LOCALE"
                :popover="{ visibility: 'focus' }"
                mode="date"
                is-range
                :is-dark="isDark">
                <template #default="{ inputValue, inputEvents }">
                  <div class="relative">
                    <svg class="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"/>
                    </svg>
                    <input type="text" readonly
                      :value="inputValue.start === inputValue.end
                        ? inputValue.start
                        : inputValue.start + ' – ' + inputValue.end"
                      v-on="inputEvents.start"
                      class="w-full pl-10 pr-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-[#22272E] text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer" />
                  </div>
                </template>
              </v-date-picker>
            </div>

            <!-- Color picker -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2.5">Color</label>
              <div class="flex flex-wrap gap-2.5">
                <button v-for="color in COLORS" :key="color.name"
                  type="button"
                  @click="form.color = color.name"
                  class="w-9 h-9 rounded-full transition-all relative flex-shrink-0"
                  :style="{ backgroundColor: color.hex }"
                  :class="form.color === color.name ? 'ring-2 ring-offset-2 ring-blue-500 scale-110' : 'hover:scale-105'"
                  :title="color.label">
                  <svg v-if="form.color === color.name"
                    class="absolute inset-0 m-auto w-5 h-5 text-white drop-shadow-sm"
                    fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 0 1 0 1.414l-8 8a1 1 0 0 1-1.414 0l-4-4a1 1 0 0 1 1.414-1.414L8 12.586l7.293-7.293a1 1 0 0 1 1.414 0Z" clip-rule="evenodd"/>
                  </svg>
                </button>
              </div>
            </div>

            <!-- Footer -->
            <div class="flex gap-3 pt-1">
              <button type="button" @click="formModal.open = false"
                class="flex-1 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-xl transition-colors">
                Cancel
              </button>
              <button type="submit" :disabled="submitting"
                class="flex-1 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 rounded-xl transition-colors flex items-center justify-center gap-2">
                <svg v-if="submitting" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                </svg>
                {{ submitting
                  ? (formModal.isEdit ? 'Updating...' : 'Adding...')
                  : (formModal.isEdit ? 'Update holiday' : 'Add holiday') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Transition>

    <!-- ── Delete confirmation modal ── -->
    <Transition name="modal">
      <div v-if="deleteModal.open"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
        @click.self="deleteModal.open = false">
        <div class="absolute inset-0 bg-black/40 backdrop-blur-sm"></div>
        <div class="relative bg-white dark:bg-[#1C2128] rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 max-w-sm w-full p-6 animate-in">
          <div class="w-12 h-12 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mx-auto mb-4">
            <svg class="w-6 h-6 text-red-500" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/>
            </svg>
          </div>
          <h3 class="text-lg font-bold text-gray-900 dark:text-white text-center mb-2">Delete holiday?</h3>
          <p class="text-sm text-gray-500 dark:text-gray-400 text-center mb-6">
            "<strong class="text-gray-700 dark:text-gray-300">{{ deleteModal.holiday?.name }}</strong>" will be permanently removed.
          </p>
          <div class="flex gap-3">
            <button @click="deleteModal.open = false" type="button"
              class="flex-1 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-xl transition-colors">
              Cancel
            </button>
            <button @click="doDelete" :disabled="deleting" type="button"
              class="flex-1 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-60 rounded-xl transition-colors flex items-center justify-center gap-2">
              <svg v-if="deleting" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
              </svg>
              {{ deleting ? 'Deleting...' : 'Yes, delete' }}
            </button>
          </div>
        </div>
      </div>
    </Transition>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRequest } from 'composables/useRequest.js'
import { useHolidaysStore } from 'stores/holidays.js'
import { useConstants } from 'composables/useConstants.js'
import FlashNotifications from 'controllers/vue_components/flash_toast'

const flash = ref(null)
const { axiosRequest } = useRequest(flash)
const store = useHolidaysStore()
const { CURRENT_LOCALE } = useConstants()

const COLORS = [
  { name: 'tomato',    hex: '#d50000', label: 'Tomato' },
  { name: 'flamingo',  hex: '#e67c73', label: 'Flamingo' },
  { name: 'tangerine', hex: '#f4511e', label: 'Tangerine' },
  { name: 'banana',    hex: '#f6bf26', label: 'Banana' },
  { name: 'sage',      hex: '#33b679', label: 'Sage' },
  { name: 'basil',     hex: '#0b8043', label: 'Basil' },
  { name: 'peacock',   hex: '#039be5', label: 'Peacock' },
  { name: 'blueberry', hex: '#3f51b5', label: 'Blueberry' },
  { name: 'lavender',  hex: '#7986cb', label: 'Lavender' },
  { name: 'grape',     hex: '#8e24aa', label: 'Grape' },
  { name: 'graphite',  hex: '#616161', label: 'Graphite' },
  { name: 'silver',    hex: '#b0b0b0', label: 'Silver' },
]

const COLOR_MAP = Object.fromEntries(COLORS.map(c => [c.name, c.hex]))


const isDark = ref(document.documentElement.classList.contains('dark'))

const loading = computed(() => store.loading)
const error = computed(() => store.error)

const submitting = ref(false)
const deleting = ref(false)
const formKey = ref(0)

const BLANK_FORM = () => ({
  id: null,
  name: '',
  color: 'peacock',
})

const form = ref(BLANK_FORM())
const targetDate = ref({ start: new Date(), end: new Date() })

const formModal = ref({ open: false, isEdit: false })
const deleteModal = ref({ open: false, holiday: null })

// ── Search + date filter + sort ──
function localDateStr(date) {
  const d = new Date(date)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function sixMonthsEnd() {
  const d = new Date()
  d.setMonth(d.getMonth() + 6)
  return new Date(d.getFullYear(), d.getMonth() + 1, 0)
}

const DEFAULT_FROM = new Date()
const DEFAULT_TO   = sixMonthsEnd()

const searchQuery = ref('')
const sortBy      = ref('date_asc')
const dateRange   = ref({ start: DEFAULT_FROM, end: DEFAULT_TO })

// Compact "May 17 – Nov 30" label for the picker button
const dateRangeLabel = computed(() => {
  const fmt = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  const s = dateRange.value.start
  const e = dateRange.value.end
  if (!s) return 'Select period'
  if (!e || localDateStr(s) === localDateStr(e)) return fmt(s)
  return `${fmt(s)} – ${fmt(e)}`
})

const filteredHolidays = computed(() => {
  let list = store.holidays

  if (searchQuery.value.trim()) {
    const q = searchQuery.value.toLowerCase()
    list = list.filter(h => h.name.toLowerCase().includes(q))
  }

  const from = dateRange.value.start ? localDateStr(dateRange.value.start) : null
  const to   = dateRange.value.end   ? localDateStr(dateRange.value.end)   : null
  if (from) list = list.filter(h => (h.end_at   || '').slice(0, 10) >= from)
  if (to)   list = list.filter(h => (h.start_at || '').slice(0, 10) <= to)

  if (sortBy.value === 'date_desc') {
    list = [...list].sort((a, b) => (b.start_at || '').localeCompare(a.start_at || ''))
  } else if (sortBy.value === 'name_asc') {
    list = [...list].sort((a, b) => a.name.localeCompare(b.name))
  } else if (sortBy.value === 'name_desc') {
    list = [...list].sort((a, b) => b.name.localeCompare(a.name))
  }
  // date_asc is the backend default — no re-sort needed

  return list
})

const hasActiveFilters = computed(() =>
  searchQuery.value.trim() !== '' ||
  sortBy.value !== 'date_asc' ||
  localDateStr(dateRange.value.start) !== localDateStr(DEFAULT_FROM) ||
  localDateStr(dateRange.value.end)   !== localDateStr(DEFAULT_TO)
)

function clearFilters() {
  searchQuery.value = ''
  sortBy.value      = 'date_asc'
  dateRange.value   = { start: new Date(), end: sixMonthsEnd() }
}

onMounted(async () => {
  await store.fetchIfStale()
})

function colorHex(colorName) {
  return COLOR_MAP[colorName] || '#616161'
}

function openAddModal() {
  form.value = BLANK_FORM()
  targetDate.value = { start: new Date(), end: new Date() }
  formKey.value++
  formModal.value = { open: true, isEdit: false }
}

function openEditModal(holiday) {
  form.value = { id: holiday.id, name: holiday.name, color: holiday.color }
  targetDate.value = { start: new Date(holiday.start_at), end: new Date(holiday.end_at) }
  formKey.value++
  formModal.value = { open: true, isEdit: true }
}

function openDeleteModal(holiday) {
  deleteModal.value = { open: true, holiday }
}

function toLocalDateStr(date) {
  const d = new Date(date)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

async function submitForm() {
  submitting.value = true
  const payload = {
    holiday: {
      name: form.value.name,
      color: form.value.color,
      start_at: toLocalDateStr(targetDate.value.start),
      end_at: toLocalDateStr(targetDate.value.end),
    },
  }

  const isEdit = formModal.value.isEdit
  const url = isEdit ? `/holidays/${form.value.id}` : '/holidays'
  const method = isEdit ? 'PUT' : 'POST'

  const res = await axiosRequest(method, url, payload, false)
  if (!res.js_error && res.response.success) {
    store.setFromMutationResponse(res.response.holidays)
    formModal.value.open = false
    flash.value?.show(res.response.message || (isEdit ? 'Holiday updated' : 'Holiday added'), 'notice')
  } else {
    flash.value?.show(res.response?.error || 'Failed to save holiday', 'alert')
  }
  submitting.value = false
}

async function doDelete() {
  if (!deleteModal.value.holiday) return
  deleting.value = true
  const res = await axiosRequest('DELETE', `/holidays/${deleteModal.value.holiday.id}`, {}, false)
  if (!res.js_error && res.response.success) {
    store.setFromMutationResponse(res.response.holidays)
    deleteModal.value.open = false
    flash.value?.show(res.response.message || 'Holiday deleted', 'notice')
  } else {
    flash.value?.show('Failed to delete holiday', 'alert')
  }
  deleting.value = false
}
</script>

<style scoped>
.modal-enter-active { transition: opacity 0.2s ease; }
.modal-leave-active { transition: opacity 0.15s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
.modal-enter-active .animate-in { transition: transform 0.2s ease; }
.modal-enter-from .animate-in { transform: scale(0.96) translateY(8px); }
</style>
